import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { postsApi, wellApi, movementStoriesApi } from '../api/client';
import { useTranslation } from '../i18n';
import { shortName } from '../utils/displayName';
import './ReactionsSheet.css';

const PAGE_SIZE = 50;

// Map Well "type" → emoji glyph for the tab badges, mirroring WellReplies.js.
const WELL_TYPE_TO_EMOJI = { praying: '🙏', heart: '❤️', fire: '🔥', amen: '🙌' };

export default function ReactionsSheet({ scope, id, initialFilter, initialCounts, onClose }) {
  // scope: 'post' | 'well-post' | 'well-thread' | 'movement'
  const { t } = useTranslation('reactions');
  const navigate = useNavigate();

  const [filter,     setFilter]     = useState(initialFilter || null); // emoji or well-type
  const [counts,     setCounts]     = useState(initialCounts || {});
  const [users,      setUsers]      = useState([]);
  const [cursor,     setCursor]     = useState(null);
  const [hasMore,    setHasMore]    = useState(true);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);

  const listRef     = useRef(null);
  const sentinelRef = useRef(null);

  const isWell  = scope === 'well-post' || scope === 'well-thread';
  const wellArg = scope === 'well-thread' ? 'thread' : 'post';

  // ── Fetch a page of reactors ────────────────────────────────────────────────
  const fetchPage = useCallback(async (resetCursor) => {
    setLoading(true);
    setError(null);
    try {
      const opts = { limit: PAGE_SIZE, cursor: resetCursor ? null : cursor };
      let res;
      if (isWell) {
        opts.type = filter || undefined;
        res = await wellApi.reactors(wellArg, id, opts);
      } else if (scope === 'movement') {
        opts.emoji = filter || undefined;
        res = await movementStoriesApi.reactors(id, opts);
      } else {
        opts.emoji = filter || undefined;
        res = await postsApi.reactors(id, opts);
      }
      setUsers(prev => resetCursor ? (res.users || []) : [...prev, ...(res.users || [])]);
      setCursor(res.nextCursor || null);
      setHasMore(!!res.nextCursor);
      if (res.counts) setCounts(res.counts);
    } catch (e) {
      setError(e.message || 'Could not load reactions');
    } finally {
      setLoading(false);
    }
  }, [cursor, filter, id, isWell, wellArg]);

  // Reset whenever the filter or target changes. fetchPage intentionally
  // omitted from deps — it depends on cursor, which this effect resets.
  useEffect(() => {
    setUsers([]);
    setCursor(null);
    setHasMore(true);
    fetchPage(true);
  }, [filter, id, scope]); // eslint-disable-line

  // Infinite scroll via IntersectionObserver.
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) fetchPage(false);
    }, { root: listRef.current, threshold: 0.1 });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, loading, fetchPage]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const total = Object.values(counts).reduce((s, n) => s + n, 0);

  const tabs = [
    { key: null, label: t('all') || 'All', glyph: null, count: total },
    ...Object.entries(counts)
      .filter(([, n]) => n > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([key, n]) => ({
        key,
        glyph: isWell ? (WELL_TYPE_TO_EMOJI[key] || key) : key,
        label: null,
        count: n,
      })),
  ];

  function viewProfile(u) {
    if (!u?.id) return;
    onClose?.();
    navigate(`/members/${u.id}`);
  }

  function rowEmoji(u) {
    if (isWell) return WELL_TYPE_TO_EMOJI[u.type] || '';
    return u.emoji || '';
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal rxs-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('title') || 'Reactions'}
      >
        <div className="rxs-grabber" />

        <div className="rxs-header">
          <h3>{t('title') || 'Reactions'}{total > 0 ? ` · ${total}` : ''}</h3>
          <button className="rxs-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="rxs-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.key ?? 'all'}
              role="tab"
              aria-selected={filter === tab.key}
              className={`rxs-tab${filter === tab.key ? ' active' : ''}`}
              onClick={() => setFilter(tab.key)}
            >
              {tab.glyph && <span className="rxs-tab-emoji">{tab.glyph}</span>}
              {tab.label && <span>{tab.label}</span>}
              <span className="rxs-tab-count">{tab.count}</span>
            </button>
          ))}
        </div>

        <div className="rxs-list" ref={listRef}>
          {error && <div className="rxs-empty">{error}</div>}

          {!error && !loading && users.length === 0 && (
            <div className="rxs-empty">{t('empty') || 'No reactions yet.'}</div>
          )}

          {users.map((u) => {
            const initials = (u.name || u.username || '?').slice(0, 1).toUpperCase();
            return (
              <button
                key={`${u.id}-${u.type || u.emoji || ''}`}
                className="rxs-row"
                onClick={() => viewProfile(u)}
              >
                {u.avatar ? (
                  <img className="rxs-avatar" src={u.avatar} alt="" />
                ) : (
                  <div className="rxs-avatar rxs-avatar-fallback">{initials}</div>
                )}
                <div className="rxs-info">
                  <span className="rxs-name">{shortName(u.name) || u.username || 'Member'}</span>
                  {u.username && <span className="rxs-handle">@{u.username}</span>}
                </div>
                <span className="rxs-emoji" aria-hidden="true">{rowEmoji(u)}</span>
              </button>
            );
          })}

          {hasMore && <div ref={sentinelRef} className="rxs-loader">
            {loading && <span className="spinner" />}
          </div>}
          {!hasMore && users.length > 0 && (
            <div className="rxs-end">{t('end') || '— end —'}</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
